import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { QuickActionMenuProps } from './types';

const QuickActionMenu: React.FC<QuickActionMenuProps> = ({
  onEssentialOnly,
  onShowAll,
  onHideAll,
  onResetToOriginal,
  isEditing
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        Quick Actions
        <ChevronDownIcon className={`ml-2 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="py-1" role="none">
            <button
              onClick={() => handleAction(onEssentialOnly)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              role="menuitem"
            >
              <div className="flex items-center">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-yellow-100 text-yellow-600 mr-3">
                  ⚡
                </span>
                <div>
                  <div className="font-medium">Essential Only</div>
                  <div className="text-xs text-gray-500">Name, email, phone, company</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleAction(onShowAll)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              role="menuitem"
            >
              <div className="flex items-center">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-green-100 text-green-600 mr-3">
                  ✓
                </span>
                <div>
                  <div className="font-medium">Show All Fields</div>
                  <div className="text-xs text-gray-500">Make all fields visible</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleAction(onHideAll)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              role="menuitem"
            >
              <div className="flex items-center">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-red-100 text-red-600 mr-3">
                  ✕
                </span>
                <div>
                  <div className="font-medium">Hide All Fields</div>
                  <div className="text-xs text-gray-500">Hide all fields</div>
                </div>
              </div>
            </button>

            {isEditing && onResetToOriginal && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => handleAction(onResetToOriginal)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  role="menuitem"
                >
                  <div className="flex items-center">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-100 text-gray-600 mr-3">
                      ↺
                    </span>
                    <div>
                      <div className="font-medium">Reset to Original</div>
                      <div className="text-xs text-gray-500">Restore initial configuration</div>
                    </div>
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActionMenu;
